import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { KanbanColumn } from 'src/sections/@dashboard/negocios';
import { persistCard, persistColumn } from 'src/redux/slices/crm';
import { useDispatch, useSelector } from 'src/redux/store';
import { validaLabelMenuDatas, verifyRules } from 'src/utils/functions';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { getListaColunasCrm, getListaLeads, setLeadData } from 'src/redux/slices/crm';
import { fToNow } from 'src/utils/formatTime';
import { formataNumeroCelular } from 'src/utils/functions';
import useAuth from 'src/hooks/useAuth';
import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import v2_actionEditarChat from 'src/actions/v2/chats/v2_actionEditarChat';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import v2_actionEditarLead from 'src/actions/v2/crm/v2_actionEditarLead';
import v2_actionDetalhesLead from 'src/actions/v2/crm/v2_actionDetalhesLead';
import v2_actionGerarExcelRelatorioNegocios from 'src/actions/v2/dashboard/relatorios/excel/v2_actionGerarExcelRelatorioNegocios';
import ModalNovoNegocio from 'src/sections/@dashboard/cadastro/ModalNovoNegocio';
import ModalNegocio from 'src/sections/@dashboard/negocios/ModalNegocio';
import TarefaDialog from 'src/pages/dashboard/Tarefas/TarefaDialog';
import tarefasService from 'src/services/tarefasService';
import v2_actionCrmPesquisarLeads from 'src/actions/v2/crm/v2_actionCrmPesquisarLeads';
import v2_actionListaTagsChats from 'src/actions/v2/chats/v2_actionListaTagsChats';
import { getChatInfoByChatId, getMensagensWhatsappDb, limparChats, setChatInfo, setMessage } from 'src/redux/slices/chat';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import Iconify from 'src/components/Iconify';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from 'src/@/components/ui/table';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Checkbox } from 'src/@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'src/@/components/ui/dialog';
import { Eye, Search, X } from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from 'src/@/components/ui/select';
import { ReloadIcon } from "@radix-ui/react-icons";
import MenuDatas2 from 'src/components/MenuDatas2';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { ZuiTooltip } from 'src/@/components/use/tooltip';
import NegocioTagPill from 'src/sections/@dashboard/negocios/components/NegocioTagPill';
import { resolveTags, resolveStatus } from 'src/utils/crmTagColors';
import { NEGOCIOS_DESIGN as D } from './Negocios.design';
import v2_actionDeletarLeadsMassa from 'src/actions/v2/crm/v2_actionDeletarLeadsMassa';

import 'moment/locale/pt-br';
moment.locale('pt-br');

// ----------------------------------------------------------------------

// Definição das colunas da tabela
const TABLE_HEAD = [
  { id: 'nome', label: 'Nome' },
  { id: 'nomeEstagio', label: 'Estágio' },
  { id: 'nomeUsuario', label: 'Responsável' },
  { id: 'status_venda', label: 'Status da Venda' },
  { id: 'tags', label: 'Tags' },
  { id: 'qualificacao', label: 'Qualificação' },
  { id: 'valor', label: 'Valor' },
  { id: 'data_criacao', label: 'Data Criação' },
  { id: 'data_modificacao', label: 'Última Atividade' },
  { id: 'whatsapp_numero', label: 'Número WhatsApp' },
  { id: 'whatsapp_nome', label: 'Nome Contato' },
  { id: '' },
];

export default function Negocios() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { colunas, ordemColunas, lead, leadsKanban } = useSelector((state) => state.crm);

  // Estados para tabela
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Estados para filtros da tabela
  const [loadingKanban, setLoadingKanban] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filterNome, setFilterNome] = useState('');
  const [filterEstagio, setFilterEstagio] = useState('');
  const [filterUsuario, setFilterUsuario] = useState('');
  const [filterQualificacao, setFilterQualificacao] = useState('');
  const [filterValor, setFilterValor] = useState('');
  const { negocio } = useSelector((state) => state.global);
  const { user, cliente } = useAuth();
  const podeVerTodosLeads = verifyRules(user.rules, "supervisionar_leads") || verifyRules(user.rules, "negocios.verTodos");
  const [searchParams, setSearchParams] = useSearchParams();

  // Estados locais com valores padrão da URL
  const [inicial, setInicial] = useState(new Date());
  const [final, setFinal] = useState(new Date());
  const inicialRef = useRef(inicial);
  const finalRef = useRef(final);
  const [periodo, setPeriodo] = useState(searchParams.get('periodo') || 'mes_atual');
  const [listaClientesCanais, setListaClientesCanais] = useState([]);
  const [pipelineId, setPipelineId] = useState(searchParams.get('pipeline_id') || '');
  const [configCrm, setConfigCrm] = useState(null);
  const [qualificacao, setQualificacao] = useState(searchParams.get('qualificacao') || 'all');
  const [responsaveis, setResponsaveis] = useState(searchParams.get('responsaveis') || 'me');
  const [loadingModalCriarNegocio, setLoadingModalCriarNegocio] = useState(false);
  const [loadingExportExcel, setLoadingExportExcel] = useState(false);
  const [openModalCriarNegocio, setOpenModalCriarNegocio] = useState(false);
  const [openDrawerNegocio, setOpenDrawerNegocio] = useState(false);
  const [tarefaDialogOpen, setTarefaDialogOpen] = useState(false);
  const [tarefaSelecionada, setTarefaSelecionada] = useState(null);
  const [tarefaLead, setTarefaLead] = useState(null);
  const [tarefaWorkflows, setTarefaWorkflows] = useState([]);
  const [filterConvert, setFilterConvert] = useState(searchParams.get('filterConvert') || null);
  const [filterClosed, setFilterClosed] = useState(searchParams.get('filterClosed') || 'all');
  const [listaUsuarios, setListaUsuarios] = useState([]);
  const [usuarioSelected, setUsuarioSelected] = useState(searchParams.get('usuarioSelected') || null);
  const [chatTags, setChatTags] = useState(() => {
    const raw = searchParams.get('chat_tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [listaChatTags, setListaChatTags] = useState([]);
  const [leadTags, setLeadTags] = useState(() => {
    const raw = searchParams.get('lead_tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [statusVendaFiltro, setStatusVendaFiltro] = useState(() => {
    const raw = searchParams.get('status_venda');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [viewMode, setViewMode] = useState('kanban');
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkMoving, setBulkMoving] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Preserva o scroll horizontal do kanban ao abrir/fechar o drawer de negócio
  // (Radix Dialog aplica overflow:hidden no body, e a posição do scroll é perdida)
  const kanbanScrollContainerRef = useRef(null);
  const kanbanScrollLeftRef = useRef(0);

  // Busca de lead (input com dropdown)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const debounceRef = useRef(null);

  // Estados para o MenuDatas2
  const [anchorElMenuDatas, setAnchorElMenuDatas] = useState(null);
  const openMenuDatas = Boolean(anchorElMenuDatas);

  // Função para atualizar a URL com os parâmetros.
  // `replace` existe para o abre/fecha do modal de negócio não empilhar uma
  // entrada de histórico a cada clique.
  const updateURLParams = (newParams, { replace = false } = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    setSearchParams(params, { replace });
  };

  // Função para ler parâmetros da URL e atualizar estado
  const readURLParams = () => {
    const urlPeriodo = searchParams.get('periodo');
    const urlPipelineId = searchParams.get('pipeline_id');
    const urlQualificacao = searchParams.get('qualificacao');
    const urlResponsaveis = searchParams.get('responsaveis');
    const urlFilterConvert = searchParams.get('filterConvert');
    const urlFilterClosed = searchParams.get('filterClosed');
    const urlUsuarioSelected = searchParams.get('usuarioSelected');
    const urlChatTags = searchParams.get('chat_tags');
    const urlLeadTags = searchParams.get('lead_tags');
    const urlStatusVenda = searchParams.get('status_venda');

    if (urlPeriodo) setPeriodo(urlPeriodo);
    if (urlPipelineId) setPipelineId(urlPipelineId);
    if (urlQualificacao) setQualificacao(urlQualificacao);
    if (urlResponsaveis) setResponsaveis(urlResponsaveis);
    if (urlFilterConvert) setFilterConvert(urlFilterConvert);
    if (urlFilterClosed) setFilterClosed(urlFilterClosed);
    if (urlUsuarioSelected) setUsuarioSelected(urlUsuarioSelected);
    if (urlChatTags) setChatTags(urlChatTags.split(',').filter(Boolean));
    if (urlLeadTags) setLeadTags(urlLeadTags.split(',').filter(Boolean));
    if (urlStatusVenda) setStatusVendaFiltro(urlStatusVenda.split(',').filter(Boolean));
  };

  // Monta a querystring do funil. Centralizado porque quatro pontos da tela
  // disparam o mesmo fetch e antes duplicavam a string à mão — divergir entre
  // as cópias era o que fazia um filtro novo "não pegar" em algum caminho.
  const buildFunilQuery = (overrides = {}) => {
    const params = {
      periodo,
      usuario_id: user.id,
      cliente_id: user.clienteId,
      data_inicial: moment(inicial).format('YYYY-MM-DD'),
      data_final: moment(final).format('YYYY-MM-DD'),
      pipeline_id: pipelineId,
      qualificacao,
      responsaveis,
      chat_tags: chatTags.length > 0 ? chatTags.join(',') : '',
      lead_tags: leadTags.length > 0 ? leadTags.join(',') : '',
      status_venda: statusVendaFiltro.length > 0 ? statusVendaFiltro.join(',') : '',
      ...overrides,
    };
    return `?${Object.entries(params).map(([k, v]) => `${k}=${v}`).join('&')}`;
  };

  async function buscarConfigCrm(querys) {
    try {
      const result = await v2_actionConfigCrm(cliente._id, querys);
      if (result.data.data) {
        let data = result.data.data;
        if (data.pipelines.length > 0 && !pipelineId) {
          const newPipelineId = data.pipelines[0]._id;
          setPipelineId(newPipelineId);
          updateURLParams({ pipeline_id: newPipelineId });
          handleListaChatsFunil(periodo, newPipelineId, qualificacao, responsaveis);
        }
        setConfigCrm(result.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function buscarConfigCrmSemAlterarPipeline(querys) {
    try {
      const result = await v2_actionConfigCrm(cliente._id, querys);
      if (result.data.data) {
        setConfigCrm(result.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const buscarUsuarios = async () => {
    try {
      const res = await getActionListaUsuariosByClienteId(user.clienteId);
      setListaUsuarios(res.data.usuarios);
    } catch (error) {
      console.log(error);
    }
  };

  const buscarChatTags = async () => {
    try {
      const res = await v2_actionListaTagsChats(`?cliente_id=${user.clienteId}`);
      setListaChatTags(res.data?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  async function buscarListaClientesCanais() {
    try {
      const response_clientes_canais = await actionListaClientesCanais(cliente._id);
      setListaClientesCanais(response_clientes_canais.data.data);
    } catch (err) {
      console.log(err);
    }
  }

  // paramOverrides existe porque quem acabou de mexer num filtro ainda não tem o
  // novo valor no estado (setState é assíncrono): sem ele, esta função regravaria
  // na URL o valor anterior e o filtro não sobreviveria a um reload.
  async function handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, querysData, showLoader = true, paramOverrides = {}) {
    try {
      if (showLoader) setIsLoadingData(true);

      let querys = querysData || buildFunilQuery({ periodo, pipeline_id: pipelineId, qualificacao, responsaveis });

      updateURLParams({
        periodo,
        pipeline_id: pipelineId,
        qualificacao,
        responsaveis,
        filterConvert,
        filterClosed,
        usuarioSelected,
        chat_tags: chatTags.length > 0 ? chatTags.join(',') : '',
        lead_tags: leadTags.length > 0 ? leadTags.join(',') : '',
        status_venda: statusVendaFiltro.length > 0 ? statusVendaFiltro.join(',') : '',
        ...paramOverrides
      });

      await Promise.all([
        dispatch(getListaLeads(querys)),
        dispatch(getListaColunasCrm(querys))
      ]);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoadingData(false);
    }
  }

  // Reabre o negócio quando a URL traz lead_id: voltar do editor de propostas,
  // F5 ou link compartilhado. Precisa rodar depois do buscarConfigCrm inicial —
  // senão o setConfigCrm do fluxo padrão sobrescreve o config do pipeline do
  // lead restaurado e o modal abre com os estágios errados.
  async function restaurarLeadDaURL() {
    const leadIdUrl = searchParams.get('lead_id');
    if (!leadIdUrl) return;
    try {
      const result = await v2_actionDetalhesLead(leadIdUrl);
      const data = result.data?.data;
      // Lead inexistente/excluído: tira o id da URL, senão o param fica preso e
      // toda recarga refaz a mesma busca que já falhou.
      if (!data) {
        updateURLParams({ lead_id: null }, { replace: true });
        return;
      }
      dispatch(setLeadData(data));
      await buscarConfigCrmSemAlterarPipeline(`?pipeline_id=${data.crm_pipeline_id}`);
      setOpenDrawerNegocio(true);
    } catch (error) {
      console.log(error);
      updateURLParams({ lead_id: null }, { replace: true });
    }
  }

  useEffect(() => {
    readURLParams();
    buscarListaClientesCanais();
    buscarUsuarios();
    buscarChatTags();
    handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis);
    (async () => {
      await buscarConfigCrm();
      await restaurarLeadDaURL();
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!user?.clienteId) return;
    tarefasService.listarWorkflows(user.clienteId).then((res) => {
      setTarefaWorkflows(res.data || []);
    }).catch(() => {});
  }, [user?.clienteId]);

  async function editarLead(action, value, leadId) {
    if (action === "editar_estagio_funil") {
      let data = {
        action: action,
        scope: {
          estagio_pipeline_id: value,
        }
      };
      await v2_actionEditarLead(leadId, data, user.id);
      enqueueSnackbar('Estágio alterado com sucesso', { variant: 'success', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
    }
  }

  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;

    if (type === 'column') return;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const start = colunas[source.droppableId];
    const finish = colunas[destination.droppableId];
    const previousColunas = colunas;

    if (start.id === finish.id) {
      flushSync(() => {
        dispatch(
          persistCard({
            ...colunas,
            [start.id]: { ...start, leadIds: [...start.leadIds] },
          })
        );
      });
      return;
    }

    const startCardIds = start.leadIds.filter((id) => id !== draggableId);
    const finishCardIds = finish.leadIds.includes(draggableId)
      ? finish.leadIds
      : [...finish.leadIds, draggableId];

    flushSync(() => {
      dispatch(
        persistCard({
          ...colunas,
          [start.id]: { ...start, leadIds: startCardIds },
          [finish.id]: { ...finish, leadIds: finishCardIds },
        })
      );
    });

    try {
      await editarLead("editar_estagio_funil", destination.droppableId, draggableId);
      handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, null, false);
    } catch (err) {
      dispatch(persistCard(previousColunas));
      enqueueSnackbar('Não foi possível mover o lead. Tente novamente.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      });
    }
  };

  // O negócio aberto vive só em state local (openDrawerNegocio) + Redux, então
  // qualquer navegação que saia desta rota perdia o contexto — era o que fazia o
  // "Voltar" do editor de propostas não reencontrar o negócio. Espelhar o id na
  // URL aqui (ponto por onde passam todos os caminhos de abertura) resolve isso
  // e de quebra torna o modal recuperável em reload/link compartilhado.
  async function handlOpenLead(data) {
    dispatch(setLeadData(data));
    updateURLParams({ lead_id: data?._id }, { replace: true });
  }

  // Refs "latest" para expor callbacks com identidade estável ao Kanban sem
  // fechar sobre valores desatualizados: cada ref é reatribuída a cada render
  // para sempre apontar para a versão mais recente da função correspondente,
  // enquanto o wrapper memoizado (useCallback) só muda quando os filtros
  // realmente mudam — evitando que todas as colunas/cards re-renderizem a
  // cada dispatch (ex.: durante o drag-and-drop do Kanban).
  const handleListaChatsFunilRef = useRef(handleListaChatsFunil);
  handleListaChatsFunilRef.current = handleListaChatsFunil;

  const handlOpenLeadRef = useRef(handlOpenLead);
  handlOpenLeadRef.current = handlOpenLead;

  const buscarConfigCrmSemAlterarPipelineRef = useRef(buscarConfigCrmSemAlterarPipeline);
  buscarConfigCrmSemAlterarPipelineRef.current = buscarConfigCrmSemAlterarPipeline;

  const handleRefreshLeadsKanban = useCallback(() => {
    handleListaChatsFunilRef.current(periodo, pipelineId, qualificacao, responsaveis, null, false);
  }, [periodo, pipelineId, qualificacao, responsaveis]);

  const handleSelectCard = useCallback((cardId) => {
    const leadContent = leadsKanban?.[cardId];
    if (!leadContent) return;
    handlOpenLeadRef.current(leadContent);
    const querys = `?pipeline_id=${leadContent.crm_pipeline_id}`;
    buscarConfigCrmSemAlterarPipelineRef.current(querys);
    setOpenDrawerNegocio(true);
  }, [leadsKanban]);

  const handleClickTarefa = useCallback((tarefa, leadCard) => {
    if (!tarefa?._id) return;
    setTarefaLead(leadCard || null);
    setTarefaSelecionada(tarefa);
    setTarefaDialogOpen(true);
  }, []);

  const handleSaveTarefaKanban = async (data) => {
    if (!tarefaSelecionada?._id) return null;
    if (tarefaLead?._id) data.lead_id = tarefaLead._id;
    try {
      const response = await tarefasService.editar(tarefaSelecionada._id, data);
      if (response.error) {
        enqueueSnackbar(response.message || 'Não foi possível salvar a tarefa', { variant: 'error' });
        return null;
      }
      enqueueSnackbar('Tarefa atualizada', { variant: 'success' });
      handleRefreshLeadsKanban();
      return response.data;
    } catch (error) {
      enqueueSnackbar('Não foi possível salvar a tarefa', { variant: 'error' });
      return null;
    }
  };

  const handleSelectLead = (leadData) => {
    if (leadData.whatsapp) {
      dispatch(getMensagensWhatsappDb(leadData.whatsapp.sessao, leadData.whatsapp.numero, 1, 10));
    }
    handlOpenLead(leadData);

    let querys = `?pipeline_id=${leadData.crm_pipeline_id}`;
    buscarConfigCrmSemAlterarPipeline(querys);
    setOpenDrawerNegocio(true);

    setShowSearchDropdown(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleExportToExcel = async () => {
    try {
      setLoadingExportExcel(true);
      const exportUserId = usuarioSelected || user.id;
      const querys = buildFunilQuery({ usuario_id: exportUserId });

      let body;
      if (viewMode === 'table') {
        const visibleLeads = getTableData();
        if (!visibleLeads || visibleLeads.length === 0) {
          enqueueSnackbar('Nenhum lead para exportar com os filtros atuais', {
            variant: 'warning',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          });
          return;
        }
        const leadIds = visibleLeads
          .map((lead) => lead?._id || lead?.id)
          .filter(Boolean);
        if (leadIds.length === 0) {
          enqueueSnackbar('Nenhum lead para exportar com os filtros atuais', {
            variant: 'warning',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          });
          return;
        }
        body = { lead_ids: leadIds };
      }

      const response = await v2_actionGerarExcelRelatorioNegocios(querys, body);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `negocios_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log('Erro ao exportar negócios:', error);
      enqueueSnackbar('Erro ao exportar negócios. Tente novamente.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
      });
    } finally {
      setLoadingExportExcel(false);
    }
  };

  // Efeito de busca (debounce)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!searchTerm || searchTerm.trim().length < 3) {
      setSearchResults([]);
      setSearchError(null);
      setShowSearchDropdown(!!searchTerm);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError(null);

        const queryParams = new URLSearchParams({
          cliente_id: user.clienteId,
          termo_pesquisa: searchTerm.trim(),
          limite: 20
        });

        const querys = `?${queryParams.toString()}`;
        const response = await v2_actionCrmPesquisarLeads(querys);
        const leads = response.data?.data || [];
        setSearchResults(Array.isArray(leads) ? leads : Object.values(leads));
        setShowSearchDropdown(true);
      } catch (e) {
        setSearchError('Não foi possível buscar.');
        setSearchResults([]);
        setShowSearchDropdown(true);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setPage(1);
  }, [filterNome, filterEstagio, filterUsuario, filterQualificacao, filterValor]);

  // Restaura o scroll horizontal do kanban depois que o drawer fecha.
  // O Radix Dialog aplica overflow:hidden + padding-right no body enquanto aberto;
  // ao desfazer isso na animação de fechamento, o scrollLeft do container é zerado.
  useEffect(() => {
    if (openDrawerNegocio) return;
    const target = kanbanScrollLeftRef.current;
    if (!target) return;

    let cancelled = false;
    const restore = () => {
      if (cancelled) return;
      const el = kanbanScrollContainerRef.current;
      if (el && el.scrollLeft !== target) {
        el.scrollLeft = target;
      }
    };

    const raf = requestAnimationFrame(restore);
    const t1 = setTimeout(restore, 50);
    const t2 = setTimeout(restore, 250);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [openDrawerNegocio]);

  // Função para converter dados do kanban para tabela
  const getTableData = () => {
    if (!leadsKanban) return [];

    const allLeads = Object.values(leadsKanban);

    let filteredLeads = allLeads.filter((lead) => {
      if (!lead) return false;

      if (usuarioSelected && String(lead.usuario_id) !== String(usuarioSelected)) return false;

      if (filterConvert === 1 && lead.convertido !== 1) return false;
      if (filterConvert === 2 && lead.convertido !== 2) return false;
      if (filterClosed === true && lead.closed !== true) return false;
      if (filterClosed === false && lead.closed !== false) return false;

      if (filterNome && !lead.nome?.toLowerCase().includes(filterNome.toLowerCase())) return false;
      if (filterEstagio && !lead.nomeEstagio?.toLowerCase().includes(filterEstagio.toLowerCase())) return false;
      if (filterUsuario && !lead.nomeUsuario?.toLowerCase().includes(filterUsuario.toLowerCase())) return false;
      if (filterQualificacao && lead.qualificacao !== parseInt(filterQualificacao)) return false;
      if (filterValor && !lead.valor?.toString().includes(filterValor)) return false;

      return true;
    });

    return filteredLeads;
  };

  const tableData = getTableData();
  const pagedTableData = tableData.slice((page - 1) * limit, (page - 1) * limit + limit);
  const pageLeadIds = pagedTableData.map((row) => row._id).filter(Boolean);
  const allCurrentPageSelected = pageLeadIds.length > 0 && pageLeadIds.every((id) => selectedLeadIds.includes(id));
  const someCurrentPageSelected = pageLeadIds.some((id) => selectedLeadIds.includes(id));

  const pipelineStages = (() => {
    const stagesFallback = Array.isArray(ordemColunas)
      ? ordemColunas
        .map((id) => ({ _id: id, nome: colunas?.[id]?.title || colunas?.[id]?.nome || colunas?.[id]?.label || id }))
        .filter((s) => s.nome)
      : [];
    return (configCrm?.stages?.length ? configCrm.stages : stagesFallback) || [];
  })();

  const allLeadsArray = leadsKanban ? Object.values(leadsKanban) : [];
  const uniqueEstagios = Array.from(new Set(allLeadsArray.map((l) => l?.nomeEstagio).filter(Boolean)));
  const uniqueUsuarios = Array.from(new Set(allLeadsArray.map((l) => l?.nomeUsuario).filter(Boolean)));

  const [showEstagioFilter, setShowEstagioFilter] = useState(false);
  const [showUsuarioFilter, setShowUsuarioFilter] = useState(false);

  const handleChangePage = (newPage) => setPage(newPage);
  const handleChangeLimit = (newLimit) => { setLimit(newLimit); setPage(1); };

  const toggleSelectLead = (id) => {
    setSelectedLeadIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSelectAllPage = () => {
    if (allCurrentPageSelected) {
      setSelectedLeadIds((prev) => prev.filter((id) => !pageLeadIds.includes(id)));
      return;
    }
    setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...pageLeadIds])));
  };

  const handleBulkMoveStage = async (estagioId) => {
    if (!estagioId || selectedLeadIds.length === 0) return;
    setBulkMoving(true);
    try {
      for (const leadId of selectedLeadIds) {
        await v2_actionEditarLead(leadId, {
          action: 'editar_estagio_funil',
          scope: { estagio_pipeline_id: estagioId },
        }, user.id);
      }
      enqueueSnackbar(`${selectedLeadIds.length} negócio(s) movido(s)`, { variant: 'success', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
      setSelectedLeadIds([]);
      handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis);
    } catch (error) {
      enqueueSnackbar('Não foi possível mover os negócios selecionados', { variant: 'error', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
    } finally {
      setBulkMoving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeadIds.length === 0 || !user?.clienteId) return;
    setBulkDeleting(true);
    try {
      const response = await v2_actionDeletarLeadsMassa(user.clienteId, selectedLeadIds);
      if (response.data?.error) {
        enqueueSnackbar(response.data?.mensagem || 'Não foi possível excluir os negócios', { variant: 'error', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
        return;
      }
      const deleted = response.data?.deleted ?? selectedLeadIds.length;
      enqueueSnackbar(`${deleted} negócio(s) excluído(s)`, { variant: 'success', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
      setSelectedLeadIds([]);
      setBulkDeleteOpen(false);
      handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis);
    } catch (error) {
      enqueueSnackbar('Não foi possível excluir os negócios selecionados', { variant: 'error', autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } });
    } finally {
      setBulkDeleting(false);
    }
  };

  const clearAllFilters = () => {
    setFilterNome('');
    setFilterEstagio('');
    setFilterUsuario('');
    setFilterQualificacao('');
    setFilterValor('');
    setPage(1);
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const getQualificacaoColor = (qual) => {
    switch (qual) {
      case 1: return 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20';
      case 2: return 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20';
      case 3: return 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-500/20';
      case 4: return 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20';
      case 5: return 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-200 dark:border-violet-500/20';
      default: return 'bg-gray-50 dark:bg-[#383838] text-gray-600 dark:text-[#ABABAD] border-gray-200 dark:border-[#383838]';
    }
  };

  const getQualificacaoLabel = (qual) => {
    switch (qual) {
      case 1: return 'Baixa';
      case 2: return 'Média';
      case 3: return 'Alta';
      case 4: return 'Muito Alta';
      case 5: return 'Crítica';
      default: return 'N/D';
    }
  };

  const totalPages = Math.ceil(tableData.length / limit);

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <ModalNovoNegocio
        open={openModalCriarNegocio}
        cliente={cliente}
        user={user}
        callbackAddContact={() => { }}
        onClose={() => {
          setOpenModalCriarNegocio(false);
          setLoadingModalCriarNegocio(false);
        }}
      />

      <ModalNegocio
        open={openDrawerNegocio}
        onClose={() => {
          setOpenDrawerNegocio(false);
          dispatch(setChatInfo(null));
          dispatch(limparChats([]));
          dispatch(setLeadData(null));
          updateURLParams({ lead_id: null }, { replace: true });
        }}
        cliente={cliente}
        user={user}
        configCrm={configCrm}
        lead={lead}
        callbackRefreshLeads={() => handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, null, false)}
        onSelect={(cardId) => {
          let leadContent = leadsKanban[cardId];
          handlOpenLead(leadContent);
          let querys = `?pipeline_id=${leadContent.crm_pipeline_id}`;
          buscarConfigCrmSemAlterarPipeline(querys);
          setOpenDrawerNegocio(true);
          dispatch(getMensagensWhatsappDb(leadContent.clienteWhatsapp.sessao, leadContent.clienteWhatsapp.numero, 1, 10));
        }}
      />

      <TarefaDialog
        open={tarefaDialogOpen}
        onClose={() => {
          setTarefaDialogOpen(false);
          setTarefaSelecionada(null);
          setTarefaLead(null);
        }}
        onSave={handleSaveTarefaKanban}
        task={tarefaSelecionada}
        workflows={tarefaWorkflows}
        selectedWorkflowId={tarefaSelecionada?.workflow_id?._id || tarefaSelecionada?.workflow_id || tarefaWorkflows[0]?._id || ''}
        leadId={tarefaLead?._id}
        lead={tarefaLead}
        lockLead
      />

      <Dialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleting && setBulkDeleteOpen(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir negócios</DialogTitle>
            <DialogDescription>
              Excluir {selectedLeadIds.length} negócio(s) selecionado(s)? Essa ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={bulkDeleting} onClick={() => setBulkDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={bulkDeleting}
              onClick={handleBulkDelete}
            >
              {bulkDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.headerRow}>
            <h1 className={D.title}>Negócios</h1>

            <div className={`${D.filterField} sm:w-[185px]`}>
              <MenuDatas2
                anchorEl={anchorElMenuDatas}
                open={openMenuDatas}
                onClose={() => setAnchorElMenuDatas(null)}
                inicial={inicial}
                final={final}
                content={validaLabelMenuDatas(periodo, inicial, final)}
                customButton={
                  <Button
                    variant="outline"
                    className={`${D.btnGhost} w-full justify-between font-normal`}
                    onClick={(e) => setAnchorElMenuDatas(e.currentTarget)}
                  >
                    <span className="truncate">{validaLabelMenuDatas(periodo, inicial, final)}</span>
                    <Iconify icon="mdi:calendar" className="ml-2 h-3.5 w-3.5 opacity-50" />
                  </Button>
                }
                changeInicial={newValue => {
                  setInicial(newValue);
                  inicialRef.current = newValue;
                  updateURLParams({ data_inicial: moment(newValue).format("YYYY-MM-DD") });
                }}
                changeFinal={newValue => {
                  setFinal(newValue);
                  finalRef.current = newValue;
                  updateURLParams({ data_final: moment(newValue).format("YYYY-MM-DD") });
                }}
                onSelect={value => { }}
                onPesquisar={() => {
                  let inicio2 = moment(inicialRef.current).format("YYYY-MM-DD");
                  let final2 = moment(finalRef.current).format("YYYY-MM-DD");
                  setPeriodo('custom');
                  updateURLParams({ periodo: 'custom', data_inicial: inicio2, data_final: final2 });
                  let querys = `?periodo=custom&usuario_id=${user.id}&cliente_id=${user.clienteId}&data_inicial=${inicio2}&data_final=${final2}&pipeline_id=${pipelineId}&qualificacao=${qualificacao}&responsaveis=${responsaveis}`;
                  handleListaChatsFunil('custom', pipelineId, qualificacao, responsaveis, querys);
                }}
              />
            </div>

            <ZuiTooltip content="O período é aplicado sobre a data da última modificação do lead (não a data de criação).">
              <span className="inline-flex cursor-help -ml-1">
                <Iconify icon="eva:info-outline" className="h-3.5 w-3.5 text-[#a1a1aa] hover:text-[#6d28d9]" />
              </span>
            </ZuiTooltip>

            <div className={D.filterField}>
              <Select
                value={pipelineId}
                onValueChange={(value) => {
                  setPipelineId(value);
                  updateURLParams({ pipeline_id: value });
                  const querys = buildFunilQuery({
                    pipeline_id: value,
                    usuario_id: usuarioSelected || user.id,
                    responsaveis: usuarioSelected ? null : responsaveis,
                  });
                  handleListaChatsFunil(periodo, value, qualificacao, responsaveis, querys);
                }}
              >
                <SelectTrigger className={D.select}>
                  <SelectValue placeholder="Selecione o funil" />
                </SelectTrigger>
                <SelectContent>
                  {configCrm?.pipelines?.map((pipeline) => (
                    <SelectItem key={pipeline._id} value={pipeline._id}>{pipeline.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Visualização */}
            {podeVerTodosLeads && (
              <div className={D.filterField}>
                <Select
                  value={responsaveis}
                  onValueChange={(value) => {
                    setUsuarioSelected(null);
                    setResponsaveis(value);
                    updateURLParams({ responsaveis: value, usuarioSelected: null });
                    handleListaChatsFunil(periodo, pipelineId, qualificacao, value);
                  }}
                >
                  <SelectTrigger className={D.select}>
                    <SelectValue placeholder="Visualização" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">Meus Leads</SelectItem>
                    <SelectItem value="all">Todos Leads</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Mais Filtros */}
            <div className="flex items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={D.filterChip}>
                    Mais filtros
                    {(filterConvert !== null || (filterClosed !== 'all' && filterClosed !== null) || (qualificacao !== 'all') || (usuarioSelected !== null) || chatTags.length > 0 || leadTags.length > 0 || statusVendaFiltro.length > 0) && (
                      <span className={D.filterCount}>
                        {(filterConvert !== null ? 1 : 0) + ((filterClosed !== 'all' && filterClosed !== null) ? 1 : 0) + (qualificacao !== 'all' ? 1 : 0) + (usuarioSelected !== null ? 1 : 0) + (chatTags.length > 0 ? 1 : 0) + (leadTags.length > 0 ? 1 : 0) + (statusVendaFiltro.length > 0 ? 1 : 0)}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={D.popover} align="start">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className={D.popoverTitle}>Filtros do lead</h4>

                      <div className="space-y-3">
                        {podeVerTodosLeads && (
                          <div>
                            <label className={D.filterLabel}>Por Usuário</label>
                            <Select
                              value={usuarioSelected || "clear"}
                              onValueChange={(value) => {
                                if (value === 'clear') {
                                  setUsuarioSelected(null);
                                  handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, null, true, { usuarioSelected: null });
                                  return;
                                }
                                setUsuarioSelected(value);
                                const querys = buildFunilQuery({ usuario_id: value, responsaveis: null });
                                handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, querys, true, { usuarioSelected: value });
                              }}
                            >
                              <SelectTrigger className={D.select}>
                                <SelectValue placeholder="Selecione um usuário" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="clear">Limpar filtro</SelectItem>
                                {listaUsuarios.map((item) => (
                                  <SelectItem key={item._id} value={item._id}>{item.nomeFantasia}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        <div>
                          <label className={D.filterLabel}>Qualificação</label>
                          <Select
                            value={qualificacao}
                            onValueChange={(value) => {
                              setQualificacao(value);
                              updateURLParams({ qualificacao: value });
                              handleListaChatsFunil(periodo, pipelineId, value, responsaveis);
                            }}
                          >
                            <SelectTrigger className={D.select}>
                              <SelectValue placeholder="Qualificação" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas as Qualificações</SelectItem>
                              {configCrm?.qualificacoes?.map((item) => (
                                <SelectItem key={item.value} value={item.value}>{item.nome}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className={D.filterLabel}>Status Venda</label>
                          <Select
                            value={filterConvert?.toString() || "all"}
                            onValueChange={(value) => {
                              let v = value === "all" ? null : parseInt(value);
                              setFilterConvert(v);
                              updateURLParams({ filterConvert: v });
                            }}
                          >
                            <SelectTrigger className={D.select}>
                              <SelectValue placeholder="Status Venda" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas as Vendas</SelectItem>
                              <SelectItem value="1">Venda Realizada</SelectItem>
                              <SelectItem value="2">Perda</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className={D.filterLabel}>Status do Lead</label>
                          <Select
                            value={filterClosed?.toString()}
                            onValueChange={(value) => {
                              let v = value === "true" ? true : value === "false" ? false : "all";
                              setFilterClosed(v);
                              updateURLParams({ filterClosed: v });
                            }}
                          >
                            <SelectTrigger className={D.select}>
                              <SelectValue placeholder="Status Aberto" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas</SelectItem>
                              <SelectItem value="false">Em Aberto</SelectItem>
                              <SelectItem value="true">Finalizados</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <label className={D.filterLabel}>Status da Venda</label>
                          <MultiSelect
                            options={(configCrm?.statusVenda || []).map((s) => ({
                              value: s._id,
                              label: s.nome,
                            }))}
                            defaultValue={statusVendaFiltro}
                            onValueChange={(values) => {
                              setStatusVendaFiltro(values);
                              const param = values && values.length > 0 ? values.join(',') : '';
                              const querys = buildFunilQuery({
                                usuario_id: usuarioSelected || user.id,
                                responsaveis: usuarioSelected ? null : responsaveis,
                                status_venda: param,
                              });
                              handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, querys, true, { status_venda: param });
                            }}
                            placeholder="Selecione o status"
                            variant="default"
                            maxCount={2}
                            className="text-xs border-gray-200 dark:border-[#383838] bg-slate-50 dark:bg-[#1A1D21]"
                          />
                        </div>

                        <div>
                          <label className={D.filterLabel}>Tags do Negócio</label>
                          <MultiSelect
                            options={(configCrm?.tagsNegocio || []).map((tag) => ({
                              value: tag._id,
                              label: tag.nome,
                            }))}
                            defaultValue={leadTags}
                            onValueChange={(values) => {
                              setLeadTags(values);
                              const param = values && values.length > 0 ? values.join(',') : '';
                              const querys = buildFunilQuery({
                                usuario_id: usuarioSelected || user.id,
                                responsaveis: usuarioSelected ? null : responsaveis,
                                lead_tags: param,
                              });
                              handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, querys, true, { lead_tags: param });
                            }}
                            placeholder="Selecione tags"
                            variant="default"
                            maxCount={2}
                            className="text-xs border-gray-200 dark:border-[#383838] bg-slate-50 dark:bg-[#1A1D21]"
                          />
                          <p className={D.popoverHint}>Tags aplicadas ao card, independentes do chat.</p>
                        </div>
                      </div>
                    </div>

                    {/* Coluna Direita — Filtros do Chat */}
                    <div className="space-y-4 border-l border-[#f0f0f3] pl-5">
                      <h4 className={D.popoverTitle}>Filtros do chat</h4>

                      <div className="space-y-3">
                        <div>
                          <label className={D.filterLabel}>Tags do Chat</label>
                          <MultiSelect
                            options={listaChatTags.map((tag) => ({
                              value: tag._id,
                              label: tag.nome,
                            }))}
                            defaultValue={chatTags}
                            onValueChange={(values) => {
                              setChatTags(values);
                              const param = values && values.length > 0 ? values.join(',') : '';
                              const querys = buildFunilQuery({
                                usuario_id: usuarioSelected || user.id,
                                responsaveis: usuarioSelected ? null : responsaveis,
                                chat_tags: param,
                              });
                              handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis, querys, true, { chat_tags: param });
                            }}
                            placeholder="Selecione tags"
                            variant="default"
                            maxCount={2}
                            className="text-xs border-gray-200 dark:border-[#383838] bg-slate-50 dark:bg-[#1A1D21]"
                          />
                          <p className={D.popoverHint}>Filtra leads cujo chat vinculado tem as tags selecionadas.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Pesquisa */}
            <div className={D.searchWrap} onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setShowSearchDropdown(false);
              }
            }}>
              <Iconify icon="fluent:search-12-filled" className={D.searchIcon} width={16} />
              <Input
                className={D.search}
                placeholder="Buscar lead..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchDropdown(true);
                }}
                onFocus={() => setShowSearchDropdown(!!searchTerm)}
              />

              {/* Dropdown de Resultados */}
              {showSearchDropdown && (
                <div className={D.searchDropdown}>
                  <div className={D.searchDropdownHead}>
                    <span>
                      {searchLoading ? 'Buscando...' : searchResults.length > 0 ? `${searchResults.length} resultado${searchResults.length !== 1 ? 's' : ''}` : 'Pesquisa'}
                    </span>
                    {searchTerm && (
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setSearchTerm(''); setSearchResults([]); setShowSearchDropdown(false); }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-[#D1D2D3] transition-colors"
                      >
                        <Iconify icon="eva:close-outline" width={14} />
                      </button>
                    )}
                  </div>

                  <div className="overflow-y-auto flex-1">
                    {searchLoading ? (
                      <div className="p-4 space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center gap-3 animate-pulse">
                            <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#383838] shrink-0" />
                            <div className="flex-1 space-y-1.5">
                              <div className="h-3 w-2/3 bg-gray-100 dark:bg-[#383838] rounded" />
                              <div className="h-2.5 w-1/3 bg-gray-100 dark:bg-[#383838] rounded" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : searchError ? (
                      <div className="p-6 text-center">
                        <Iconify icon="eva:alert-circle-outline" width={24} className="text-red-400 mx-auto mb-2" />
                        <p className="text-xs text-red-500">{searchError}</p>
                      </div>
                    ) : searchTerm.trim().length < 3 ? (
                      <div className="p-6 text-center">
                        <Iconify icon="eva:search-outline" width={24} className="text-gray-300 dark:text-[#ABABAD]/30 mx-auto mb-2" />
                        <p className="text-xs text-gray-400 dark:text-[#ABABAD]">Digite 3+ caracteres para buscar</p>
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-6 text-center">
                        <Iconify icon="eva:inbox-outline" width={24} className="text-gray-300 dark:text-[#ABABAD]/30 mx-auto mb-2" />
                        <p className="text-xs text-gray-400 dark:text-[#ABABAD]">Nenhum lead encontrado</p>
                      </div>
                    ) : (
                      <div className="p-1.5">
                        {searchResults.map((item) => (
                          <div
                            key={item._id}
                            className="px-3 py-2.5 hover:bg-violet-50 dark:hover:bg-violet-500/10 rounded-lg cursor-pointer transition-all group"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSelectLead(item);
                            }}
                          >
                            <div className="flex items-start gap-2.5">
                              {/* Avatar / Iniciais */}
                              <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-500/15 flex items-center justify-center text-[10px] font-bold text-violet-600 dark:text-violet-400 shrink-0 mt-0.5">
                                {item?.nome ? item.nome.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'}
                              </div>

                              <div className="flex-1 min-w-0">
                                {/* Nome + Valor */}
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-[13px] font-semibold text-gray-900 dark:text-[#D1D2D3] truncate group-hover:text-[#7C3AED] transition-colors">
                                    {item?.nome || 'Sem nome'}
                                  </p>
                                  {item?.valor > 0 && (
                                    <span className="text-[11px] font-bold text-gray-900 dark:text-[#D1D2D3] shrink-0">
                                      R$ {item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </span>
                                  )}
                                </div>

                                {/* Info linha */}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {item?.whatsapp?.numero && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-[#ABABAD]">
                                      <Iconify icon="ri:whatsapp-fill" className="text-green-500" width={11} />
                                      {formataNumeroCelular(item.whatsapp.numero)}
                                    </span>
                                  )}
                                  {item?.contato?.telefone && !item?.whatsapp?.numero && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-[#ABABAD]">
                                      <Iconify icon="eva:phone-outline" width={11} />
                                      {formataNumeroCelular(item.contato.telefone)}
                                    </span>
                                  )}
                                  {item?.nomeEstagio && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded bg-violet-100/80 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400">
                                      {item.nomeEstagio}
                                    </span>
                                  )}
                                  {item?.nomeUsuario && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-gray-400 dark:text-[#ABABAD]/70">
                                      <Iconify icon="mdi:account-outline" width={11} />
                                      {item.nomeUsuario}
                                    </span>
                                  )}
                                </div>

                                {/* Data */}
                                {item?.data_modificacao && (
                                  <p className="text-[10px] text-gray-400 dark:text-[#ABABAD]/60 mt-1">
                                    Atualizado {fToNow(item.data_modificacao)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={D.topbarActions}>
              <div className={D.viewToggle}>
                <button
                  type="button"
                  onClick={() => { setViewMode('kanban'); setSelectedLeadIds([]); }}
                  className={viewMode === 'kanban' ? D.viewToggleBtnActive : D.viewToggleBtn}
                  title="Kanban"
                >
                  <Iconify icon="mdi:view-column-outline" width={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={viewMode === 'table' ? D.viewToggleBtnActive : D.viewToggleBtn}
                  title="Tabela"
                >
                  <Iconify icon="mdi:format-list-bulleted" width={16} />
                </button>
              </div>
              <button
                type="button"
                className={D.btnIcon}
                onClick={handleExportToExcel}
                disabled={loadingExportExcel}
                title="Exportar Excel"
              >
                {loadingExportExcel ? <ReloadIcon className="animate-spin h-4 w-4" /> : <Iconify icon="file-icons:microsoft-excel" className="h-4 w-4" />}
              </button>
              <button type="button" onClick={() => setOpenModalCriarNegocio(true)} className={D.btnPrimary}>
                Novo negócio
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={D.pageBody}>
        {isLoadingData ? (
          viewMode === 'kanban' ? (
            // Kanban Skeleton
            <div className={D.kanbanRow}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="min-w-[230px] flex-1 rounded-[12px] bg-white border border-[#e7e7ec] p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-200 dark:bg-[#383838] animate-pulse" />
                      <div className="h-4 w-24 bg-gray-200 dark:bg-[#383838] rounded animate-pulse" />
                    </div>
                    <div className="h-5 w-5 bg-gray-200 dark:bg-[#383838] rounded-full animate-pulse" />
                  </div>
                  <div className="space-y-2.5">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="bg-slate-50 dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-3.5 animate-pulse">
                        <div className="flex items-center justify-between mb-3">
                          <div className="h-3 w-16 bg-gray-200 dark:bg-[#383838] rounded" />
                          <div className="h-3 w-12 bg-gray-200 dark:bg-[#383838] rounded" />
                        </div>
                        <div className="h-4 w-3/4 bg-gray-200 dark:bg-[#383838] rounded mb-2" />
                        <div className="h-3 w-1/2 bg-gray-200 dark:bg-[#383838] rounded mb-3" />
                        <div className="flex items-center justify-between pt-2 border-t border-gray-50 dark:border-[#383838]/50">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-[#383838]" />
                            <div className="h-3 w-20 bg-gray-200 dark:bg-[#383838] rounded" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Table Skeleton
            <div className={D.emptyCard}>
              <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#6d28d9]" />
              <p className={D.emptySub}>Carregando negócios...</p>
            </div>
          )
        ) : viewMode === 'kanban' ? (
          // Kanban View
          leadsKanban ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="all-columns" direction="horizontal" type="column" isDropDisabled={true}>
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={(el) => {
                      provided.innerRef(el);
                      kanbanScrollContainerRef.current = el;
                    }}
                    onScroll={(e) => {
                      kanbanScrollLeftRef.current = e.currentTarget.scrollLeft;
                    }}
                    className={D.kanbanRow}
                  >
                    {!ordemColunas.length ? (
                      <div className={D.emptyCard}>
                        <div className={D.emptyIcon}>
                          <Iconify icon="mdi:view-dashboard-outline" width={28} />
                        </div>
                        <h3 className={D.emptyTitle}>Nenhum lead encontrado</h3>
                        <p className={D.emptySub}>Não encontramos nenhum lead por aqui. Tente ajustar os filtros ou criar um novo negócio.</p>
                      </div>
                    ) : (
                      colunas &&
                      ordemColunas.map((columnId, index) => (
                        <KanbanColumn
                          key={columnId}
                          user={user}
                          loadingKanban={loadingKanban}
                          cliente={cliente}
                          configCrm={configCrm}
                          callbackRefreshLeads={handleRefreshLeadsKanban}
                          index={parseInt(index, 10)}
                          column={colunas[columnId]}
                          filterConvert={filterConvert}
                          filterClosed={filterClosed}
                          tagsNegocio={configCrm?.tagsNegocio}
                          statusVenda={configCrm?.statusVenda}
                          onSelect={handleSelectCard}
                          onClickTarefa={handleClickTarefa}
                        />
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <div className={D.emptyCard}>
              <div className={D.emptyIcon}>
                <Iconify icon="mdi:view-dashboard-outline" width={28} />
              </div>
              <h3 className={D.emptyTitle}>Nenhum lead encontrado</h3>
              <p className={D.emptySub}>Não encontramos nenhum lead por aqui.</p>
            </div>
          )
        ) : (
          // Table View
          <div className={D.tableCard}>
            {tableData.length === 0 ? (
              <div className={D.emptyCard}>
                <div className={D.emptyIcon}>
                  <Iconify icon="mdi:table-off" width={28} />
                </div>
                <h3 className={D.emptyTitle}>Nenhum lead encontrado</h3>
                <p className={D.emptySub}>Nenhum lead corresponde aos filtros aplicados.</p>
                {(filterNome || filterEstagio || filterUsuario || filterQualificacao || filterValor) && (
                  <Button variant="link" className="text-[#7C3AED] font-medium" onClick={clearAllFilters}>
                    Limpar filtros da tabela
                  </Button>
                )}
              </div>
            ) : (
              <>
                {selectedLeadIds.length > 0 && (
                  <div className={D.bulkBar}>
                    <span className={D.bulkCount}>{selectedLeadIds.length} selecionado(s)</span>
                    <Select disabled={bulkMoving || bulkDeleting} onValueChange={handleBulkMoveStage}>
                      <SelectTrigger className="h-8 w-[200px] text-xs border-[#d4d4d8] bg-white">
                        <SelectValue placeholder={bulkMoving ? 'Movendo...' : 'Mover para estágio'} />
                      </SelectTrigger>
                      <SelectContent>
                        {pipelineStages.map((stage) => (
                          <SelectItem key={stage._id} value={stage._id}>{stage.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      className="h-8 px-3 rounded-lg text-[13px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      disabled={bulkDeleting || bulkMoving}
                      onClick={() => setBulkDeleteOpen(true)}
                    >
                      Excluir
                    </button>
                    <button
                      type="button"
                      className="h-8 px-3 rounded-lg text-[13px] font-semibold text-[#52525b] hover:bg-white"
                      disabled={bulkDeleting || bulkMoving}
                      onClick={() => setSelectedLeadIds([])}
                    >
                      Limpar seleção
                    </button>
                  </div>
                )}
                <div className="flex-1 min-h-0 overflow-auto">
                  <Table>
                    <TableHeader className="bg-[#fafafb] sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={allCurrentPageSelected ? true : someCurrentPageSelected ? 'indeterminate' : false}
                            onCheckedChange={toggleSelectAllPage}
                            className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                            aria-label="Selecionar página"
                          />
                        </TableHead>
                        {TABLE_HEAD.map((head) => (
                          <TableHead key={head.id} className={D.tableHead}>
                            <div className="flex items-center gap-1 relative">
                              <span>{head.label}</span>
                              {(head.id === 'nomeEstagio' || head.id === 'nomeUsuario') && (
                                <>
                                  {(() => {
                                    const isFiltered = head.id === 'nomeEstagio' ? !!filterEstagio : head.id === 'nomeUsuario' ? !!filterUsuario : false;
                                    return (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className={`h-6 w-6 p-0 ${isFiltered ? 'bg-violet-50 dark:bg-violet-500/10' : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (head.id === 'nomeEstagio') {
                                            setShowEstagioFilter((v) => !v);
                                            setShowUsuarioFilter(false);
                                          } else if (head.id === 'nomeUsuario') {
                                            setShowUsuarioFilter((v) => !v);
                                            setShowEstagioFilter(false);
                                          }
                                        }}
                                        title={`Filtrar ${head.label}`}
                                      >
                                        <Iconify icon={isFiltered ? 'mdi:filter-check' : 'mdi:filter-variant'} className={`h-4 w-4 ${isFiltered ? 'text-violet-600' : 'text-gray-600 dark:text-[#ABABAD]'}`} />
                                      </Button>
                                    );
                                  })()}
                                  {head.id === 'nomeEstagio' && showEstagioFilter && (
                                    <div className="absolute top-6 left-0 z-50 bg-slate-50 dark:bg-[#222529] border dark:border-[#383838] rounded-xl shadow-lg p-3 w-56" onClick={(e) => e.stopPropagation()}>
                                      <div className="text-[11px] text-gray-500 dark:text-[#ABABAD] mb-1.5 font-medium">Filtrar estágio</div>
                                      <Select
                                        value={filterEstagio}
                                        onValueChange={(value) => {
                                          const v = value === '__all__' ? '' : value;
                                          setFilterEstagio(v);
                                          setShowEstagioFilter(false);
                                          setPage(1);
                                        }}
                                      >
                                        <SelectTrigger className="h-8 text-xs border-gray-200 dark:border-[#383838] bg-slate-50 dark:bg-[#1A1D21] text-gray-900 dark:text-[#D1D2D3]">
                                          <SelectValue placeholder="Todos" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__all__">Todos</SelectItem>
                                          {uniqueEstagios.map((n) => (
                                            <SelectItem key={n} value={n}>{n}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                  {head.id === 'nomeUsuario' && showUsuarioFilter && (
                                    <div className="absolute top-6 left-0 z-50 bg-slate-50 dark:bg-[#222529] border dark:border-[#383838] rounded-xl shadow-lg p-3 w-56" onClick={(e) => e.stopPropagation()}>
                                      <div className="text-[11px] text-gray-500 dark:text-[#ABABAD] mb-1.5 font-medium">Filtrar responsável</div>
                                      <Select
                                        value={filterUsuario}
                                        onValueChange={(value) => {
                                          const v = value === '__all__' ? '' : value;
                                          setFilterUsuario(v);
                                          setShowUsuarioFilter(false);
                                          setPage(1);
                                        }}
                                      >
                                        <SelectTrigger className="h-8 text-xs border-gray-200 dark:border-[#383838] bg-slate-50 dark:bg-[#1A1D21] text-gray-900 dark:text-[#D1D2D3]">
                                          <SelectValue placeholder="Todos" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="__all__">Todos</SelectItem>
                                          {uniqueUsuarios.map((n) => (
                                            <SelectItem key={n} value={n}>{n}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedTableData.map((row) => (
                        <TableRow key={row._id} className={`${D.tableRow} ${selectedLeadIds.includes(row._id) ? 'bg-[#7C3AED]/5' : ''}`}>
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedLeadIds.includes(row._id)}
                              onCheckedChange={() => toggleSelectLead(row._id)}
                              className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                              aria-label={`Selecionar ${row.nome || 'negócio'}`}
                            />
                          </TableCell>
                          <TableCell className="cursor-pointer" onClick={() => {
                            handlOpenLead(row);
                            let querys = `?pipeline_id=${row.crm_pipeline_id}`;
                            buscarConfigCrmSemAlterarPipeline(querys);
                            setOpenDrawerNegocio(true);
                            if (row.clienteWhatsapp) {
                              dispatch(getMensagensWhatsappDb(row.clienteWhatsapp.sessao, row.clienteWhatsapp.numero, 1, 10));
                            }
                          }}>
                            <div className="flex items-center gap-2.5">
                              <Iconify icon="mdi:account-circle" width={16} className="text-[#7C3AED] flex-shrink-0" />
                              <span className={D.tableLink}>
                                {row.nome || 'Sem nome'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const stages = pipelineStages;
                              if (!stages.length) {
                                return (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400">
                                    {row.nomeEstagio || 'Sem estágio'}
                                  </span>
                                );
                              }
                              return (
                                <Select
                                  defaultValue={row.estagio_pipeline_id}
                                  onValueChange={async (value) => {
                                    await editarLead('editar_estagio_funil', value, row._id);
                                    handleListaChatsFunil(periodo, pipelineId, qualificacao, responsaveis);
                                  }}
                                >
                                  <SelectTrigger className="h-7 text-xs w-[180px] border-gray-200 dark:border-[#383838] bg-slate-50 dark:bg-[#1A1D21] text-gray-900 dark:text-[#D1D2D3]">
                                    <SelectValue placeholder={row.nomeEstagio || 'Sem estágio'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {stages.map((stage) => (
                                      <SelectItem key={stage._id} value={stage._id}>
                                        {stage.nome}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600 dark:text-[#ABABAD]">
                              {row.nomeUsuario || <span className="text-gray-400 italic">Não atribuído</span>}
                            </span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const status = resolveStatus(row.status_venda_id, configCrm?.statusVenda);
                              if (!status) return <span className="text-gray-300 dark:text-[#565856]">-</span>;
                              return <NegocioTagPill nome={status.nome} cor={status.cor_hex} maxWidth="max-w-[150px]" />;
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const tags = resolveTags(row.tags, configCrm?.tagsNegocio);
                              if (tags.length === 0) return <span className="text-gray-300 dark:text-[#565856]">-</span>;
                              return (
                                <div className="flex items-center flex-wrap gap-1 max-w-[200px]">
                                  {tags.map((tag) => (
                                    <NegocioTagPill key={tag._id} nome={tag.nome} cor={tag.cor_hex} />
                                  ))}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 w-fit ${getQualificacaoColor(row.qualificacao)}`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
                              {getQualificacaoLabel(row.qualificacao)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">
                              {formatCurrency(row.valor)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600 dark:text-[#ABABAD]">{formatDate(row.data_criacao)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600 dark:text-[#ABABAD]">{formatDate(row.data_modificacao)}</span>
                          </TableCell>
                          <TableCell>
                            {row.whatsapp?.numero ? (
                              <div className="flex items-center gap-1.5">
                                <Iconify icon="ri:whatsapp-fill" className="text-green-600" width={14} />
                                <span className="text-sm text-gray-600 dark:text-[#ABABAD]">{formataNumeroCelular(row.whatsapp.numero)}</span>
                              </div>
                            ) : (
                              <span className="text-gray-300 dark:text-[#565856]">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.whatsapp?.nome ? (
                              <span className="text-sm text-gray-600 dark:text-[#ABABAD] truncate max-w-[150px] inline-block" title={row.whatsapp.nome}>
                                {row.whatsapp.nome}
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-[#565856]">-</span>
                            )}
                          </TableCell>
                          <TableCell className="w-10">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg text-gray-400 dark:text-[#ABABAD] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10 opacity-0 group-hover:opacity-100 transition-all"
                              onClick={() => {
                                handlOpenLead(row);
                                let querys = `?pipeline_id=${row.crm_pipeline_id}`;
                                buscarConfigCrmSemAlterarPipeline(querys);
                                setOpenDrawerNegocio(true);
                                if (row.clienteWhatsapp) {
                                  dispatch(getMensagensWhatsappDb(row.clienteWhatsapp.sessao, row.clienteWhatsapp.numero, 1, 10));
                                }
                              }}
                              title="Ver detalhes"
                            >
                              <Iconify icon="mdi:information-outline" width={18} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className={D.pagination}>
                  <div className={D.paginationText}>
                    <span>Exibindo</span>
                    <Select value={String(limit)} onValueChange={v => { setLimit(Number(v)); setPage(1); }}>
                      <SelectTrigger className="w-[65px] h-8 rounded-lg border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] text-sm dark:text-[#D1D2D3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span>de {tableData.length} negócios</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className={D.pageBtn}
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                      Math.max(0, page - 3),
                      Math.min(totalPages, page + 2)
                    ).map(p => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={p === page ? D.pageNumActive : D.pageNum}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className={D.pageBtn}
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
